import { HAS_DISMISSED_PLAN_FEATURES_TRIAL_UPSELL_BANNER, useUserSpecificLocalStorage } from '../../hooks/use-local-storage';
import { addPeriodOfTime, getCurrentDateTime } from '../../services/datetime-service';
import { BillingPlanIds } from '../../enums/billing-plan-ids';
import { TrialTiers, type TrialTier, type PlanFeaturesTrialHistory } from './constants';

/** Duration before a dismissed banner can reappear */
const SEVEN_DAYS = { period: 'days', value: 7 };

/**
 * Manages the dismissal state of the PlanFeaturesTrialUpsellBanner.
 *
 * Uses user-specific localStorage to persist dismissal timestamps, allowing the banner
 * to reappear after a 7-day cooldown period. This prevents the banner from being
 * overly intrusive while still ensuring visibility over time.
 * @returns A tuple containing:
 *   - `isDismissed`: Whether the banner was dismissed within the last 7 days
 *   - `handleDismiss`: Callback to dismiss the banner (stores current timestamp)
 */
export const useHasDismissedBanner = (): [boolean, () => void] => {
  const [lastDismissedDateTimeISO, setLastDismissedDateTimeISO] = useUserSpecificLocalStorage(
    HAS_DISMISSED_PLAN_FEATURES_TRIAL_UPSELL_BANNER,
  );

  const handleDismiss = () => {
    setLastDismissedDateTimeISO(getCurrentDateTime().toISO());
  };

  if (!lastDismissedDateTimeISO) {
    return [false, handleDismiss];
  }

  const lastDismissalPlus7Days = addPeriodOfTime(lastDismissedDateTimeISO, SEVEN_DAYS);
  const now = getCurrentDateTime();
  const isDismissed = lastDismissalPlus7Days > now;

  return [isDismissed, handleDismiss];
};

/**
 * Determines the trial tier based on the user's current billing plan.
 * @param billingPlanId - The user's current billing plan ID
 * @returns The appropriate trial tier to offer (Standard for starter plans, Premium for others)
 */
export const getTier = (billingPlanId: number): TrialTier => {
  if (billingPlanId === BillingPlanIds.Starter2025Q2) {
    return TrialTiers.Standard;
  }

  return TrialTiers.Premium;
};

/**
 * Safely parses the trial history JSON string from seller settings.
 * @param trialHistoryJson - The raw JSON string from seller settings (may be undefined/null)
 * @returns The parsed trial history object, or an empty object if parsing fails
 */
export const parseTrialHistory = (trialHistoryJson: string | undefined | null): PlanFeaturesTrialHistory => {
  if (!trialHistoryJson) {
    return {};
  }

  try {
    return JSON.parse(trialHistoryJson) as PlanFeaturesTrialHistory;
  } catch {
    return {};
  }
};
